/*
#*===================================================================
#*
#* Licensed Materials - Property of GameTimeFootage Inc.
#*
#* Copyright NextPro 2018. All Rights Reserved.
#*
#*===================================================================
*/

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { map } from 'rxjs/operators';
import { Constants } from 'app/shared/models/constants.model';

@Injectable({ providedIn: 'root' })
export class CouponsService {
  constructor(private http: HttpClient) {}

  getCoupon(code: string) {
    return this.http.get(`${environment.apiHostname}/v1/coupons/code/${code}`).pipe(
      map((response: any) => {
        return response.data;
      })
    );
  }

  getCouponOrders(couponId: any) {
    return this.http.post(`${environment.apiHostname}/v1/orders/search`, { coupons: [couponId] }).pipe(
      map((response: any) => {
        return response.data && response.data.entities ? response.data.entities : [];
      })
    );
  }

  applyCoupon(couponData, initialPlanData, optionsName = 'options') {
    let newPrice = 0;

    switch (couponData.applicableTo) {
      case Constants.COUPON_APPLICABLE_TO_TYPE.ENTIRE_ORDER:
        const subtotal = initialPlanData[optionsName].reduce((acc, item) => {
          const optionPrice = parseFloat(item.price);
          return acc + optionPrice;
        }, 0);

        if (couponData.applicableToProducts && couponData.applicableToProducts.length) {
          /* calculate discount from applicableToProducts total only */
          const applicableToProductsTotal = initialPlanData[optionsName].reduce((acc, item) => {
            const optionPrice = this.applicableProductType(item.type, couponData) ? parseFloat(item.price) : 0;
            return acc + optionPrice;
          }, 0);
          newPrice =
            subtotal - (applicableToProductsTotal - this.calculateDiscount(applicableToProductsTotal, couponData));
          return newPrice;
        }

        /* calculate discount from order total */
        newPrice = this.calculateDiscount(subtotal, couponData);
        break;
      case Constants.COUPON_APPLICABLE_TO_TYPE.SPECIFIC_EVENT:
        newPrice = initialPlanData[optionsName].reduce((acc, item) => {
          let optionPrice = item.price;
          if (
            item.eventId &&
            couponData.eventIds.includes(item.eventId) &&
            this.applicableProductType(item.type, couponData)
          ) {
            optionPrice = this.calculateDiscount(optionPrice, couponData);
          }

          return acc + parseFloat(optionPrice);
        }, 0);

        break;
      case Constants.COUPON_APPLICABLE_TO_TYPE.ALL_EVENTS:
        newPrice = initialPlanData[optionsName].reduce((acc, item) => {
          let optionPrice = item.price;
          if (
            item.sportId &&
            item.sportId === couponData.sport.id &&
            this.applicableProductType(item.type, couponData)
          ) {
            optionPrice = this.calculateDiscount(optionPrice, couponData);
          }

          return acc + parseFloat(optionPrice);
        }, 0);
        break;
      case Constants.COUPON_APPLICABLE_TO_TYPE.HIGHLIGHT_REEL:
        newPrice = initialPlanData[optionsName].reduce((acc, item) => {
          let optionPrice = item.price;

          if (item.type === Constants.PURCHASE_TYPE.HR) {
            optionPrice = this.calculateDiscount(optionPrice, couponData);
          }

          return acc + parseFloat(optionPrice);
        }, 0);

        break;
      case Constants.COUPON_APPLICABLE_TO_TYPE.HIGHLIGHT_REEL_PRO:
        newPrice = initialPlanData[optionsName].reduce((acc, item) => {
          let optionPrice = item.price;

          if (item.type === Constants.PURCHASE_TYPE.PRO_HR) {
            optionPrice = this.calculateDiscount(optionPrice, couponData);
          }

          return acc + parseFloat(optionPrice);
        }, 0);

        break;
    }
    return newPrice;
  }

  private applicableProductType(optionType, coupon) {
    return coupon.applicableToProducts ? coupon.applicableToProducts.split(',').includes(optionType) : true;
  }

  private calculateDiscount(price, coupon): number {
    if (coupon.discountType === Constants.COUPON_DISCOUNT_TYPE.AMOUNT) {
      return Math.max(0, parseFloat(price) - parseFloat(coupon.discount));
    }
    if (coupon.discountType === Constants.COUPON_DISCOUNT_TYPE.PERCENTAGE) {
      return Math.max(0, parseFloat(price) - (parseFloat(coupon.discount) / 100) * parseFloat(price));
    }

    return parseFloat(price);
  }
}
